import { createRef, useEffect, useRef, useState } from 'react'
import FranchiseActionMenu from './FranchiseActionMenu'
import { Link } from 'react-router-dom'
import { KTSVG } from '../../helpers'
import { GET_ALL_FRANCHISE } from '../../graphql/query'
import { useQuery } from '@apollo/client'

interface IProps {
  setIsOffsetLimit: any
}

const FranchiseList: React.FC<IProps> = ({ setIsOffsetLimit }) => {
  const franchiseActionDropdownMenuRefs = useRef<any[]>([])

  const [currentPage, setCurrentPage] = useState<number>(1)
  const ViewFranchisePerPage = 10
  const ViewFranchisePerPageOffset = (currentPage - 1) * ViewFranchisePerPage

  const { data, loading, error } = useQuery(GET_ALL_FRANCHISE, {
    variables: {
      input: {
        limit: ViewFranchisePerPage,
        offset: ViewFranchisePerPageOffset
      }
    },
    context: {
      headers: {
        'access-control-allow-methods': 'Franchise'
      }
    }
  })

  useEffect(() => {
    setIsOffsetLimit(ViewFranchisePerPageOffset)
  }, [ViewFranchisePerPageOffset, setIsOffsetLimit])

  // Pagination
  const totalFranchise = data?.getAllFranchise?.count
  const totalPages = Math.ceil(totalFranchise / ViewFranchisePerPage)

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber)
  }

  const nextPage = () => {
    if (
      totalPages > 0 &&
      currentPage < totalPages &&
      data?.getAllFranchise?.franchises.length > 0
    ) {
      setCurrentPage((prevPage) => prevPage + 1)
    }
  }

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1)
    }
  }

  const firstPage = () => {
    setCurrentPage(1)
  }

  const lastPage = () => {
    setCurrentPage(totalPages)
  }

  // Generate snake handle pagination numbers
  const generatePaginationNumbers = () => {
    const numbers = []
    const maxVisibleNumbers = 4 // Maximum visible pagination numbers

    if (totalPages <= maxVisibleNumbers) {
      // If total pages are less than or equal to max visible numbers, show all
      for (let i = 1; i <= totalPages; i++) {
        numbers.push(i)
      }
    } else {
      const middleIndex = Math.ceil(maxVisibleNumbers / 2) // Middle index of the pagination numbers
      let startPage = currentPage - middleIndex + 1
      let endPage = currentPage + middleIndex - 1

      if (startPage < 1) {
        // If start page is less than 1, adjust start and end page accordingly
        endPage = startPage + maxVisibleNumbers - 1
        startPage = 1
      } else if (endPage > totalPages) {
        // If end page is greater than total pages, adjust start and end page accordingly
        startPage = endPage - maxVisibleNumbers + 1
        endPage = totalPages
      }

      for (let i = startPage; i <= endPage; i++) {
        numbers.push(i)
      }
    }

    return numbers
  }

  if (data) {
    console.log(data)
  }

  if (error) {
    console.log(error)
  }

  if (loading) {
    return <>Loading....</>
  }

  franchiseActionDropdownMenuRefs.current = data?.getAllFranchise?.franchises?.map(
    (item: any, i: number) => franchiseActionDropdownMenuRefs.current[i] ?? createRef()
  )

  function getCompanySizeLabel(companySize: any) {
    switch (companySize) {
      case 'belowTwenty':
        return 'Below 20'
      case 'fiftyOneToTwoHundred':
        return '51 to 200'
      case 'twentyOneToFifty':
        return '21 to 50'
      case 'twoHundredOneToFiveHundred':
        return '201 to 500'
      case 'fiveHundredOneToThousand':
        return '501 to 1000'
      case 'thousandPlus':
        return '1000+'
      default:
        return companySize
    }
  }

  return (
    <div className={`card mb-5 mb-xl-8`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-3 mb-1'>Franchise Details</span>
        </h3>
        <div className='card-toolbar ' title='Click to add a user'>
          <Link to='/franchise/onboarding'>
            <div className='btn btn-sm btn-light-primary d-flex'>
              <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
              Onboarding New Franchise
            </div>
          </Link>
        </div>
      </div>

      <div className='card-body py-3'>
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bolder text-muted'>
                <th className='min-w-100px'>Franchise Name</th>
                <th className='min-w-100px'>Company Size</th>
                <th className='min-w-100px'>Start Date</th>
                <th className='min-w-100px'>Registered Address</th>
                <th className='min-w-100px'>Business Contact Number</th>
                <th className='min-w-100px'>Action</th>
              </tr>
            </thead>
            <tbody>
              {data?.getAllFranchise?.franchises !== null ? (
                <>
                  {data?.getAllFranchise?.franchises.map((franchise: any, index: any) => (
                    <tr key={index}>
                      <td>
                        <div className='d-flex align-items-center'>
                          {!!franchise.name ? (
                            <div className='text-dark fw-bold text-hover-primary d-block fs-6'>
                              {franchise.name}
                            </div>
                          ) : (
                            <i className='fw-light'>No Data found</i>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className='d-flex justify-content-start flex-column'>
                          {!!franchise.companySize ? (
                            <div className='text-dark fw-bold text-hover-primary d-block fs-6'>
                              {getCompanySizeLabel(franchise.companySize)}
                            </div>
                          ) : (
                            <i className='fw-light'>No Data found</i>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className='d-flex justify-content-start flex-column'>
                          {!!franchise.dateOfIncorporation ? (
                            <div className='text-dark fw-bold text-hover-primary d-block fs-6'>
                              {franchise.dateOfIncorporation}
                            </div>
                          ) : (
                            <i className='fw-light'>No Data found</i>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className='d-flex justify-content-start flex-column'>
                          {!!franchise.registeredAddress ? (
                            <div className='text-dark fw-bold text-hover-primary d-block fs-6'>
                              {franchise.registeredAddress}
                            </div>
                          ) : (
                            <i className='fw-light'>No Data found</i>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className='d-flex justify-content-start flex-column'>
                          {!!franchise.businessContactNumber ? (
                            <div className='text-dark fw-bold text-hover-primary d-block fs-6'>
                              {franchise.businessContactNumber}
                            </div>
                          ) : (
                            <i className='fw-light'>No Data found</i>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className='d-flex justify-content-start flex-column'>
                          <div
                            id={`user-menu-${index}`}
                            ref={franchiseActionDropdownMenuRefs.current[index]}
                          >
                            <FranchiseActionMenu
                              franchiseActionDropdownMenuRefs={franchiseActionDropdownMenuRefs}
                              user={franchise}
                              index={index}
                              franchiseId={franchise.id}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </>
              ) : (
                <>
                  <p className='text-muted fst-italic mt-auto'>No data is present</p>
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className='row'>
          <div className='col-sm-12 col-md-5 d-flex align-items-center justify-content-center justify-content-md-start'></div>
          <div className='col-sm-12 col-md-7 d-flex align-items-center justify-content-center justify-content-md-end'>
            <div className='dataTables_paginate paging_simple_numbers' id='kt_table_users_paginate'>
              <ul className='pagination'>
                <li className={`paginate_button page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    type='button'
                    aria-controls='kt_table_users'
                    data-dt-idx='0'
                    className='btn primary page-link'
                    onClick={firstPage}
                  >
                    <i className='previous'></i>
                    <i className='previous'></i>
                  </button>
                </li>
                <li className={`paginate_button page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    type='button'
                    aria-controls='kt_table_users'
                    data-dt-idx='0'
                    className='btn primary page-link'
                    onClick={prevPage}
                  >
                    <i className='previous'></i>
                  </button>
                </li>
                {generatePaginationNumbers().map((pageNumber) => (
                  <li
                    key={pageNumber}
                    className={`paginate_button page-item ${
                      currentPage === pageNumber ? 'active' : ''
                    }`}
                  >
                    <button
                      type='button'
                      aria-controls='kt_table_users'
                      data-dt-idx={pageNumber}
                      className='btn primary page-link'
                      onClick={() => paginate(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  </li>
                ))}
                <li
                  className={`paginate_button page-item ${
                    currentPage === totalPages ? 'disabled' : ''
                  }`}
                >
                  <button
                    type='button'
                    aria-controls='kt_table_users'
                    data-dt-idx='0'
                    className='btn primary page-link'
                    onClick={nextPage}
                  >
                    <i className='next'></i>
                  </button>
                </li>
                <li
                  className={`paginate_button page-item ${
                    currentPage === totalPages ? 'disabled' : ''
                  }`}
                >
                  <button
                    type='button'
                    aria-controls='kt_table_users'
                    data-dt-idx='0'
                    className='btn primary page-link'
                    onClick={lastPage}
                  >
                    <i className='next'></i>
                    <i className='next'></i>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FranchiseList
